import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AppLayout from '../../components/Layout/ParentAccountLayout';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import MyPiggyIcon from '../../components/UI/MyPiggyIcon';

const CompleteSetup = () => {
  const navigate = useNavigate();

  const {
    user,
    parent,
    completeSetup,
    signOut
  } = useAuth();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  /*
   * ---------------------------------------------------------
   * CURRENT USER DISPLAY INFORMATION
   * ---------------------------------------------------------
   *
   * Parent name was saved in Supabase user metadata when the
   * account was originally created.
   */
  const displayName = useMemo(() => {
    const metadataName =
      user?.user_metadata?.name?.trim();

    if (metadataName) {
      return metadataName;
    }

    return 'Parent';
  }, [user]);

  const displayEmail =
    user?.email || '';

  const isGoogleUser = useMemo(() => {
    const providers =
      user?.app_metadata?.providers;

    return (
      user?.app_metadata?.provider ===
        'google' ||
      (
        Array.isArray(providers) &&
        providers.includes('google')
      )
    );
  }, [user]);

  /*
   * ---------------------------------------------------------
   * EXISTING PARENT REDIRECT
   * ---------------------------------------------------------
   *
   * If the family/parent setup already exists, there is nothing
   * left to complete. Send the user directly to the dashboard.
   */
  useEffect(() => {
    if (user && parent) {
      navigate('/parent', {
        replace: true
      });
    }
  }, [
    user,
    parent,
    navigate
  ]);

  /*
   * ---------------------------------------------------------
   * COMPLETE FAMILY SETUP
   * ---------------------------------------------------------
   */
  const handleComplete = async () => {
    if (loading) return;

    setLoading(true);
    setError('');

    try {
      await completeSetup();

      navigate('/parent', {
        replace: true
      });
    } catch (e) {
      console.error(
        'Complete parent setup error:',
        e
      );

      setError(
        e?.message ||
          'Failed to create your family account. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  /*
   * ---------------------------------------------------------
   * SIGN OUT
   * ---------------------------------------------------------
   */
  const handleSignOut = async () => {
    if (loading) return;

    try {
      await signOut();

      navigate('/parent/login', {
        replace: true
      });
    } catch (e) {
      console.error(
        'Sign out error:',
        e
      );

      setError(
        e?.message ||
          'Unable to sign out. Please try again.'
      );
    }
  };

  if (!user) {
    return null;
  }

  return (
    <AppLayout title="MyPiggy">
      <div className="max-w-lg mx-auto">

        {/* ---------------------------------------------------
            ACCOUNT VERIFICATION SUCCESS
        --------------------------------------------------- */}
        <div className="mb-5 bg-green-50 dark:bg-green-950/35 border border-green-200 dark:border-green-800 rounded-xl px-5 py-4">
          <div className="flex items-start gap-3">
            <MyPiggyIcon name="celebration-achievement" size="sm" />

            <div>
              <p className="font-semibold text-green-900 dark:text-green-200">
                {isGoogleUser
                  ? 'Google account verified!'
                  : 'Email confirmed!'}
              </p>

              <p className="text-sm text-green-800 dark:text-green-200 mt-1">
                {isGoogleUser
                  ? 'Your Google account has been verified successfully.'
                  : 'Your MyPiggy account has been verified successfully.'}
              </p>
            </div>
          </div>
        </div>

        {/* ---------------------------------------------------
            SIGNED-IN USER BANNER
        --------------------------------------------------- */}
        <div className="mb-5 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 shadow-sm rounded-xl px-5 py-4">
          <div className="flex items-center gap-4">
            <MyPiggyIcon name="kid-profile" size="md" />

            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-slate-300 font-medium">
                Signed in as
              </p>

              <p className="font-semibold text-gray-900 dark:text-slate-100 truncate">
                {displayName}
              </p>

              {displayEmail && (
                <p className="text-sm text-gray-600 dark:text-slate-300 break-all">
                  {displayEmail}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* ---------------------------------------------------
            FAMILY SETUP CARD
        --------------------------------------------------- */}
        <Card className="p-4 sm:p-5">
          <div className="text-center mb-4">
            <MyPiggyIcon name="family" size="lg" className="mb-4" />

            <h2 className="text-2xl font-extrabold text-gray-900 dark:text-slate-100 mb-3">
              One last step
            </h2>

            <p className="text-gray-600 dark:text-slate-300 text-lg leading-relaxed">
              Let's create your family profile so you can start using MyPiggy.
            </p>
          </div>

          <div className="bg-blue-50 dark:bg-blue-950/35 border border-blue-100 rounded-xl p-5 mb-4">
            <p className="font-semibold text-gray-900 dark:text-slate-100 mb-2">
              What happens next?
            </p>

            <p className="text-sm text-gray-600 dark:text-slate-300 leading-relaxed">
              MyPiggy will create your private family space. After that,
              you'll be able to add your children and start tracking their
              real-life piggy banks, savings goals, chores, and money activity.
            </p>
          </div>

          {error && (
            <div
              className="text-red-700 dark:text-red-300 text-sm bg-red-50 dark:bg-red-950/35 border border-red-100 p-3 rounded-lg mb-5"
              role="alert"
            >
              {error}
            </div>
          )}

          <div className="space-y-4">
            <Button
              type="button"
              className="w-full"
              onClick={handleComplete}
              disabled={loading}
            >
              {loading
                ? 'Creating your family...'
                : 'Create My Family'}
            </Button>

            <button
              type="button"
              className="w-full text-gray-500 dark:text-slate-300 hover:text-gray-800 dark:hover:text-slate-200 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleSignOut}
              disabled={loading}
            >
              Not {displayName}? Sign out
            </button>
          </div>
        </Card>

        <div className="mt-4 text-center">
          <p className="text-sm text-gray-500 dark:text-slate-300">
            Real piggy bank. Digital tracking.
            <br />
            Lifelong money lessons.
          </p>
        </div>
      </div>
    </AppLayout>
  );
};

export default CompleteSetup;

